/*
 * Props.
 */

interface ScreenshotRequestOptions {
  shouldCrop?: boolean;
  cropText: string;
  iframe: HTMLIFrameElement;
}

interface SelectionRegion {
  leftRatio: number;
  topRatio: number;
  widthRatio: number;
  heightRatio: number;
}

/*
 * Tool.
 */

export async function requestScreenshotBootToolAsync(
  options: ScreenshotRequestOptions,
): Promise<string | undefined> {
  const {iframe} = options;
  if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
    throw new Error('Screen capture is not supported in this browser');
  }

  iframe.style.visibility = 'hidden';
  try {
    return await captureScreenAsync(options);
  } finally {
    iframe.style.visibility = '';
  }
}

/*
 * Helpers.
 */

async function captureScreenAsync(options: ScreenshotRequestOptions): Promise<string | undefined> {
  const {shouldCrop = false, cropText} = options;
  const selectionRegion = shouldCrop ? await selectRegionBeforeCaptureAsync(cropText) : undefined;

  if (shouldCrop && !selectionRegion) {
    throw new Error('Screenshot capture cancelled by user');
  }

  const stream = await navigator.mediaDevices.getDisplayMedia({
    audio: false,
    video: true,
    // @ts-expect-error preferCurrentTab is experimental but makes the experience easier.
    preferCurrentTab: true,
  });
  const video = document.createElement('video');

  try {
    video.srcObject = stream;

    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('Failed to load shared screen stream'));
    });

    await video.play();

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Failed to create canvas context for screenshot capture');
    }

    context.drawImage(video, 0, 0);
    if (!selectionRegion) {
      return canvas.toDataURL('image/png');
    }

    return cropCanvasToSelectionRegion(canvas, selectionRegion);
  } finally {
    // Stop all tracks to turn off the "recording" indicator.
    stream.getTracks().forEach((track) => track.stop());
  }
}

function cropCanvasToSelectionRegion(sourceCanvas: HTMLCanvasElement, region: SelectionRegion): string {
  const sourceX = Math.max(0, Math.round(sourceCanvas.width * region.leftRatio));
  const sourceY = Math.max(0, Math.round(sourceCanvas.height * region.topRatio));
  const sourceWidth = Math.max(1, Math.round(sourceCanvas.width * region.widthRatio));
  const sourceHeight = Math.max(1, Math.round(sourceCanvas.height * region.heightRatio));

  const boundedWidth = Math.min(sourceWidth, sourceCanvas.width - sourceX);
  const boundedHeight = Math.min(sourceHeight, sourceCanvas.height - sourceY);

  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = boundedWidth;
  croppedCanvas.height = boundedHeight;

  const croppedContext = croppedCanvas.getContext('2d');
  if (!croppedContext) {
    return sourceCanvas.toDataURL('image/png');
  }

  croppedContext.drawImage(
    sourceCanvas,
    sourceX,
    sourceY,
    boundedWidth,
    boundedHeight,
    0,
    0,
    boundedWidth,
    boundedHeight,
  );

  return croppedCanvas.toDataURL('image/png');
}

async function selectRegionBeforeCaptureAsync(cropText: string): Promise<SelectionRegion | undefined> {
  return new Promise<SelectionRegion | undefined>((resolve) => {
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.zIndex = '99999999';
    overlay.style.background = 'rgba(0, 0, 0, 0.35)';
    overlay.style.cursor = 'crosshair';
    overlay.style.userSelect = 'none';
    overlay.style.touchAction = 'none';
    overlay.style.outline = 'none';
    overlay.tabIndex = -1;

    const title = document.createElement('div');
    title.textContent = cropText;
    title.style.position = 'absolute';
    title.style.top = '16px';
    title.style.left = '50%';
    title.style.transform = 'translateX(-50%)';
    title.style.padding = '8px 10px';
    title.style.background = 'rgba(0, 0, 0, 0.7)';
    title.style.color = 'white';
    title.style.fontSize = '13px';
    title.style.borderRadius = '6px';

    const selection = document.createElement('div');
    selection.style.position = 'fixed';
    selection.style.border = '2px solid #4ea1ff';
    selection.style.background = 'rgba(78, 161, 255, 0.2)';
    selection.style.pointerEvents = 'none';
    selection.style.display = 'none';

    overlay.append(title, selection);
    document.body.append(overlay);
    overlay.focus();

    let isSelecting = false;
    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;

    const cleanup = () => {
      overlay.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('keydown', onKeyDown);
      overlay.remove();
    };

    const renderSelection = () => {
      const left = Math.min(startX, endX);
      const top = Math.min(startY, endY);
      const width = Math.abs(endX - startX);
      const height = Math.abs(endY - startY);

      selection.style.display = 'block';
      selection.style.left = `${left}px`;
      selection.style.top = `${top}px`;
      selection.style.width = `${width}px`;
      selection.style.height = `${height}px`;
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) {
        return;
      }

      startX = event.clientX;
      startY = event.clientY;
      endX = event.clientX;
      endY = event.clientY;
      isSelecting = true;
      renderSelection();
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!isSelecting) {
        return;
      }

      endX = event.clientX;
      endY = event.clientY;
      renderSelection();
    };

    const onPointerUp = () => {
      if (!isSelecting) {
        return;
      }

      isSelecting = false;

      const left = Math.min(startX, endX);
      const top = Math.min(startY, endY);
      const width = Math.abs(endX - startX);
      const height = Math.abs(endY - startY);

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (width < 4 || height < 4 || viewportWidth === 0 || viewportHeight === 0) {
        cleanup();
        resolve(undefined);
        return;
      }

      const leftRatio = Math.min(1, Math.max(0, left / viewportWidth));
      const topRatio = Math.min(1, Math.max(0, top / viewportHeight));
      const widthRatio = Math.min(1 - leftRatio, Math.max(0, width / viewportWidth));
      const heightRatio = Math.min(1 - topRatio, Math.max(0, height / viewportHeight));

      cleanup();
      resolve({leftRatio, topRatio, widthRatio, heightRatio});
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      cleanup();
      resolve(undefined);
    };

    overlay.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('keydown', onKeyDown);
  });
}
